import { useQuery } from "@tanstack/react-query";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { DashboardWidget } from "../DashboardWidget";
import { getPlatformColor } from "../PlatformBadge";
import { PlatformIcon } from "../PlatformBadge";
import { formatNumber, formatPercent } from "@/lib/formatters";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

interface EngagementMetrics {
  videoViews: number;
  engagements: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  follows: number;
  profileVisits: number;
  impressions: number;
  clicks: number;
  avgWatchTime: number;
  sixSecViewRate: number;
  sixSecondViews: number;
}

export function TikTokEngagementCard({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { getUrl, dataKey } = useDashboardDataSource();
  const { startDate, endDate } = useDashboardDate();

  const { data, isLoading, error, refetch } = useQuery<EngagementMetrics>({
    queryKey: ["dashboard", "engagement-metrics-tiktok", startDate, endDate, dataKey],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate, platform_slug: "tiktok" });
      const res = await fetch(getUrl("engagement-metrics", params));
      if (!res.ok) throw new Error("Failed to fetch engagement metrics");
      return res.json();
    },
  });

  const hasData = data && data.impressions > 0;

  const ctr = hasData ? Math.round((data.clicks / data.impressions) * 10000) / 100 : 0;
  const roas = 0;

  const metrics = hasData ? [
    { label: "Video Views", value: formatNumber(data.videoViews) },
    { label: "Likes", value: formatNumber(data.likes) },
    { label: "Comments", value: formatNumber(data.comments) },
    { label: "Shares", value: formatNumber(data.shares) },
    { label: "Saves", value: formatNumber(data.saves) },
    { label: "Follows", value: formatNumber(data.follows) },
    { label: "Profile Visits", value: formatNumber(data.profileVisits) },
    { label: "Avg Watch Time", value: `${data.avgWatchTime.toFixed(1)}s` },
    { label: "6s View Rate", value: `${data.sixSecViewRate.toFixed(1)}%` },
  ] : [];

  return (
    <DashboardWidget
      title="TikTok Engagement"
      widgetType="kpi"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error instanceof Error ? error.message : null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="tiktok"
    >
      {!hasData ? (
        <div className="flex flex-col items-center justify-center h-full text-sm p-4" style={{ color: "#5F6D82" }} data-testid="tiktok-engagement-empty">
          <p>No TikTok data available</p>
        </div>
      ) : (
        <div className="flex flex-col h-full p-1" data-testid="tiktok-engagement-card">
          <div className="flex items-center gap-2 mb-3">
            <PlatformIcon platform="tiktok" size="sm" />
            <span className="text-sm font-semibold text-gray-900">TikTok Engagement</span>
          </div>

          <div className="flex-1 grid grid-cols-3 gap-1.5 overflow-auto">
            {metrics.map((m) => (
              <div key={m.label} className="bg-gray-50 rounded-lg p-1.5">
                <p className="text-[9px] text-gray-500 uppercase tracking-wider">{m.label}</p>
                <p className="text-xs font-semibold text-gray-900">{m.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-1.5 pt-1.5 border-t border-gray-100">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-gray-500">CTR</span>
              <span className={`font-medium ${ctr > 1 ? "text-green-600" : "text-orange-600"}`}>
                {formatPercent(ctr)}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] mt-0.5">
              <span className="text-gray-500">Total Engagements</span>
              <span className="font-medium text-gray-900">
                {formatNumber(data.engagements)}
              </span>
            </div>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
